import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface SubinventoryResponse {
  success: boolean;
  data?: Array<{ subinventory: string }>;
  message?: string;
}

interface UseSubinventoryLovProps {
  data: string[];
  isLoading: boolean;
  error: string | null;
  refetch: (
    orgId: string,
    callback?: (data: string[]) => void
  ) => Promise<void>;
}

type CallbackFunction = (data: string[]) => void;

export const useSubinventoryLov = (
  toOrg?: string,
  executeApiOnload = true
): UseSubinventoryLovProps => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<string[]>([]);

  const handleEmptyResponse = useCallback((callback?: CallbackFunction) => {
    const emptyData: string[] = [];
    setData(emptyData);
    callback?.(emptyData);
  }, []);

  const fetchData = useCallback(
    async (orgId: string, callback?: CallbackFunction): Promise<void> => {
      if (!orgId) {
        handleEmptyResponse(callback);
        return;
      }

      setIsLoading(true);
      setError(null);
      setData([]);

      try {
        const { data: responseData } = await apiClient.get<SubinventoryResponse>(
          API_CONFIG.ENDPOINTS.GET_SUBINVENTORY_LOV,
          {
            params: {
              to_org: String(orgId || '').trim(),
            },
          }
        );

        if (responseData.success && Array.isArray(responseData.data)) {
          const subinventoryList = responseData.data.map(
            (item) => item.subinventory
          );
          setData(subinventoryList);
          callback?.(subinventoryList);
        } else {
          handleEmptyResponse(callback);
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message;
        setError(errorMessage);
        console.error("Subinventory LOV Error:", errorMessage);
        handleEmptyResponse(callback);
      } finally {
        setIsLoading(false);
      }
    },
    [handleEmptyResponse]
  );

  // Memoized refetch function
  const refetch = useCallback(
    async (orgId: string, callback?: CallbackFunction): Promise<void> => {
      await fetchData(orgId, callback);
    },
    [fetchData]
  );

  // Initial data fetch
  useEffect(() => {
    if (executeApiOnload && toOrg) {
      fetchData(toOrg);
    }
  }, [executeApiOnload, toOrg, fetchData]);

  return { data, isLoading, error, refetch };
};

